import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import { Mail, Lock, ShieldCheck, ArrowRight, KeyRound } from "lucide-react";

export default function AdminLogin() {
  const { adminLogin, verifyAdminOtp } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const codeRef = useRef(null);

  const submitCredentials = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const res = await adminLogin(email, password);
    setSubmitting(false);
    if (res.ok) {
      setStep(2);
      if (res.dev_code) setCode(res.dev_code);
      toast.success("Verification code sent to your email");
      setTimeout(() => codeRef.current?.focus(), 100);
    } else {
      setErr(res.error);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const res = await verifyAdminOtp(email, code);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}!`);
      nav("/dashboard/admin");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo inverted /></div>
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-blue-600/20 grid place-items-center">
              <ShieldCheck className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-white">Admin Access</h1>
              <p className="text-xs text-slate-400">Restricted area · Two-factor verification required</p>
            </div>
          </div>

          {step === 1 ? (
            <form onSubmit={submitCredentials} className="space-y-4" data-testid="admin-login-form">
              <div>
                <label className="text-sm font-semibold text-slate-300">Email</label>
                <div className="mt-1.5 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="admin-login-email"
                    placeholder="admin email"
                    className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-300">Password</label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-login-password"
                    placeholder="••••••••"
                    className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  />
                </div>
              </div>
              {err && (
                <div data-testid="admin-login-error" className="text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg p-3">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="admin-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-500 disabled:opacity-60 transition-all"
              >
                {submitting ? "Verifying..." : "Continue"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          ) : (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="admin-otp-form">
              <p className="text-sm text-slate-400">
                A 6-digit verification code was sent to <span className="text-white font-semibold">{email}</span>. It expires in 5 minutes.
              </p>
              <div>
                <label className="text-sm font-semibold text-slate-300">Verification code</label>
                <div className="mt-1.5 relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    ref={codeRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="admin-otp-input"
                    placeholder="000000"
                    className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-center text-2xl tracking-[0.4em] font-bold placeholder:text-slate-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  />
                </div>
              </div>
              {err && (
                <div data-testid="admin-otp-error" className="text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg p-3">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || code.length !== 6}
                data-testid="admin-otp-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-500 disabled:opacity-60 transition-all"
              >
                {submitting ? "Verifying..." : "Verify & Sign in"} <ShieldCheck className="h-4 w-4" />
              </button>
              <button
                type="button"
                data-testid="admin-otp-back"
                onClick={() => { setStep(1); setCode(""); setErr(""); }}
                className="w-full text-sm text-slate-400 hover:text-white transition-colors"
              >
                ← Back to credentials
              </button>
            </form>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-slate-600">
          Unauthorized access attempts are logged and monitored.
        </p>
      </div>
    </div>
  );
}
